import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { Card } from '../components/common/Card';
import { ProjectForm } from '../components/ProjectForm';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import type { UpdateProjectRequest } from '../types';

export function EditProjectPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { data, isLoading, error } = useQuery({
    queryKey: ['project', id],
    queryFn: () => api.getProject(id!),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: UpdateProjectRequest) => api.updateProject(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      showToast('Project updated', 'success');
      navigate(`/project/${id}`);
    },
    onError: () => {
      showToast('Failed to update project', 'error');
    },
  });

  const project = data?.project;
  const isAdmin = user?.role === 'admin';
  const isSubmitter = user?.email === project?.submitter_id;
  const canEdit = !!project && project.status === 'DRAFT' && (isSubmitter || isAdmin);

  // Redirect away if the project exists but cannot be edited
  useEffect(() => {
    if (!isLoading && project && !canEdit) {
      showToast('This project can no longer be edited', 'error');
      navigate(`/project/${id}`);
    }
  }, [isLoading, project, canEdit, id, navigate, showToast]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <div className="p-8 text-center">
            <h2 className="text-xl font-semibold text-wolf-100 mb-2">Project Not Found</h2>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 text-action hover:text-action-light transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  if (!canEdit) {
    // Redirect is handled by the effect above; render nothing in the meantime.
    return null;
  }

  return (
    <div className="max-w-2xl mx-auto">
      <Link
        to={`/project/${id}`}
        className="inline-flex items-center gap-2 text-wolf-300 hover:text-wolf-100 transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Project
      </Link>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-wolf-100 mb-2">Edit Project</h1>
        <p className="text-wolf-300">Update your draft's title and description.</p>
      </div>

      <Card>
        <ProjectForm
          initialTitle={project.title}
          initialDescription={project.description ?? ''}
          submitLabel="Save Changes"
          cancelTo={`/project/${id}`}
          isSubmitting={updateMutation.isPending}
          onSubmit={(formData) => updateMutation.mutate(formData)}
        />
      </Card>
    </div>
  );
}
